import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useParcelling } from './useParcelling';
import { useParcellingForm } from './useParcellingForm';
import { useBindingUsers } from './useBindingUsers';
import { useParcellingStore } from '../store';
import type { Culture, CropStage } from '../constants';
import type {
	ParcellingPageConfig,
	BindingUsersResponse,
	PropDTO,
	PropDTOResponse,
	ParcelData,
} from '../types';
import { createEmptyParcelData, toFixedParcelPayload } from '../utils';
import type { HarvestPhase } from '../constants';
import { NUTRIENTS } from '../constants/nutrients.constants';
import { HARVEST_PHASES } from '../constants/phases.constants';
import { UserRole } from '@practium/shared';
import {
	usePostParcelling,
	useUpdateParcelling,
	useDeleteParcelling,
	useDuplicateParcelling,
} from '@practium/features/api';

interface ConfirmOptions {
	title: string;
	message: string;
	confirmText?: string;
	cancelText?: string;
	variant?: 'danger' | 'warning' | 'info';
}

interface UseParcellingPageOptions {
	config: ParcellingPageConfig;
	toast: {
		success: (msg: string) => void;
		error: (msg: string) => void;
	};
	confirm: (options: ConfirmOptions) => Promise<boolean>;
	fetchBindingUsers?: (query: string) => Promise<BindingUsersResponse>;
	fetchFarms?: (params?: { userId?: string; query?: string }) => Promise<PropDTOResponse>;
}

const defaultFetchBindingUsers = async (): Promise<BindingUsersResponse> => ({
	docs: [],
	pagination: { currentPage: 1, totalPages: 0, totalItems: 0, itemsPerPage: 10 },
});

export const useParcellingPage = ({
	config,
	toast,
	confirm,
	fetchBindingUsers,
	fetchFarms,
}: UseParcellingPageOptions) => {
	const { permissions, mockData, userRole, currentUserId, isMaster, autoSelectSelfAsClient } = config;
	const [activeTab, setActiveTab] = useState<'manual' | 'flora'>('manual');
	const [isEditing, setIsEditing] = useState(false);
	const [clientModalOpen, setClientModalOpen] = useState(false);
	const [farmModalOpen, setFarmModalOpen] = useState(false);
	const [selectedFarmIds, setSelectedFarmIds] = useState<string[]>([]);
	const [farms, setFarms] = useState<PropDTO[]>([]);
	const [farmsLoading, setFarmsLoading] = useState(false);
	const [isFloraExpanded, setIsFloraExpanded] = useState(false);
	const [isFloraAnimating, setIsFloraAnimating] = useState(false);
	const [floraParcels, setFloraParcels] = useState<ParcelData>(createEmptyParcelData());

	const effectiveUserRole = userRole || UserRole.Farmer;
	const isAdmin = effectiveUserRole === UserRole.Admin;
	const canManagePublicVisibility = isAdmin && !!isMaster;
	const { removeParcelling, addParcelling } = useParcellingStore();

	const postParcelling = usePostParcelling();
	const updateParcellingMutation = useUpdateParcelling();
	const deleteParcellingMutation = useDeleteParcelling();
	const duplicateParcellingMutation = useDuplicateParcelling();

	const {
		createParcelling,
		parcellings,
		selectParcelling,
		selectedParcelling,
		selectedParcellingId,
		canEditParcelling,
		canDeleteParcelling,
	} = useParcelling({
		mockData: mockData.docs,
		currentUserId,
		userRole: effectiveUserRole,
		onSuccess: (message) => {
			toast.success(message);
			setIsEditing(false);
			setActiveTab('manual');
		},
		onError: (error) => {
			toast.error(error);
		},
	});

	const {
		users: bindingUsers,
		isLoading: bindingUsersLoading,
		searchTerm: bindingSearchTerm,
		setSearchTerm: setBindingSearchTerm,
		canBind,
		getUserById: getBindingUserById,
	} = useBindingUsers({
		userRole: effectiveUserRole,
		fetchUsers: fetchBindingUsers || defaultFetchBindingUsers,
		enabled: permissions.canCreate || permissions.canEdit,
	});

	const {
		formData,
		updateField,
		updateParcels,
		handleSubmit,
		isDirty,
		isSubmitting,
		reset,
		errors,
	} = useParcellingForm({
		initialData: selectedParcelling,
		onSubmit: async (data) => {
			if (!permissions.canEdit && !permissions.canCreate) return;

			const parcelsForSave = activeTab === 'flora' ? floraParcels : data.parcels;

			const payload = {
				name: data.name,
				culture: data.culture,
				cropStage: data.cropStage,
				note: data.note,
				bindingUserIds: data.bindingUserIds.filter((id) => id !== 'all'),
				farmIds: data.farmIds.filter((id) => id !== 'all'),
				isPublic: canManagePublicVisibility ? data.isPublic : false,
				fixedParcel: toFixedParcelPayload(parcelsForSave),
			};

			if (selectedParcelling?._id.startsWith('temp-')) {
				removeParcelling(selectedParcelling._id);
				const realRecord = await postParcelling.mutateAsync(payload);
				if (realRecord?._id) {
					addParcelling(realRecord);
					selectParcelling(realRecord._id);
				}
			} else if (selectedParcelling?._id) {
				await updateParcellingMutation.mutateAsync({ _id: selectedParcelling._id, ...payload });
			}
			toast.success('Parcelamento salvo com sucesso');
			setIsEditing(false);
		},
		onError: (error) => {
			toast.error(error);
		},
	});

	const selectedCanEdit = selectedParcelling ? canEditParcelling(selectedParcelling) : false;
	const selectedCanDelete = selectedParcelling ? canDeleteParcelling(selectedParcelling) : false;

	const bindingUserDisplayName = useMemo(() => {
		const ids = formData.bindingUserIds;
		if (!ids || ids.length === 0) return '';
		if (ids.includes('all')) return 'Todos os Clientes';
		if (bindingUsers.length > 1 && ids.length === bindingUsers.length) return 'Todos os Clientes';

		const resolvedBindingUsers = selectedParcelling?.bindingUsers ?? [];
		const names = ids
			.map((id) => {
				const resolved = resolvedBindingUsers.find((u) => u._id === id);
				if (resolved) return resolved.surName ? `${resolved.name} ${resolved.surName}` : resolved.name;
				const user = getBindingUserById(id);
				return user ? (user.surName ? `${user.name} ${user.surName}` : user.name) : null;
			})
			.filter((name): name is string => Boolean(name));

		if (names.length === 0) return `${ids.length} clientes`;
		if (names.length <= 2) return names.join(', ');
		return `${names.length} clientes`;
	}, [formData.bindingUserIds, getBindingUserById, bindingUsers.length, selectedParcelling]);

	const selectedFarmName = useMemo(() => {
		if (selectedFarmIds.length === 0) return '';
		if (selectedFarmIds.includes('all')) return 'Todas as Fazendas';
		if (farms.length > 0 && selectedFarmIds.length === farms.length) return 'Todas as Fazendas';

		const resolvedFarms = selectedParcelling?.farms ?? [];
		const names = selectedFarmIds.map((id) => {
			const resolved = resolvedFarms.find((f) => f._id === id);
			if (resolved) return resolved.name;
			const farm = farms.find((f) => f._id === id);
			return farm ? farm.name : id;
		});
		if (names.length <= 2) return names.join(', ');
		return `${names.length} fazendas`;
	}, [selectedFarmIds, farms, selectedParcelling]);

	const canBindFarm = !!(fetchFarms && (permissions.canCreate || permissions.canEdit));

	useEffect(() => {
		if (!fetchFarms || !canBindFarm) return;
		setFarmsLoading(true);
		fetchFarms()
			.then((res) => setFarms(res.docs))
			.finally(() => setFarmsLoading(false));
	}, [fetchFarms, canBindFarm]);

	useEffect(() => {
		setSelectedFarmIds(selectedParcelling?.farmIds || []);
	}, [selectedParcelling]);

	const handleOpenClientModal = useCallback(() => {
		setClientModalOpen(true);
	}, []);

	const handleCloseClientModal = useCallback(() => {
		setClientModalOpen(false);
	}, []);

	const handleClientSelect = useCallback(
		(userIds: string[]) => {
			updateField('bindingUserIds', userIds);
			if (userIds.includes('all') && canManagePublicVisibility) {
				updateField('isPublic', true);
			}
		},
		[updateField, canManagePublicVisibility]
	);

	const handleOpenFarmModal = useCallback(() => {
		setFarmModalOpen(true);
	}, []);

	const handleCloseFarmModal = useCallback(() => {
		setFarmModalOpen(false);
	}, []);

	const handleFarmSelect = useCallback(
		(farmIds: string[]) => {
			setSelectedFarmIds(farmIds);
			updateField('farmIds', farmIds);
		},
		[updateField]
	);

	const handleToggleFloraExpand = useCallback(() => {
		setIsFloraExpanded((prev) => !prev);
	}, []);

	const updateFloraParcels = useCallback(
		(parcels: ParcelData) => {
			setFloraParcels(parcels);
			updateParcels(parcels);
		},
		[updateParcels]
	);

	const floraTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

	useEffect(() => {
		return () => {
			floraTimersRef.current.forEach(clearTimeout);
		};
	}, []);

	const startFloraFill = useCallback(() => {
		floraTimersRef.current.forEach(clearTimeout);
		floraTimersRef.current = [];

		setFloraParcels(createEmptyParcelData());
		setIsFloraAnimating(true);

		const floraPhases: HarvestPhase[] = Array.from(
			{ length: 12 },
			(_, i) => HARVEST_PHASES[i % HARVEST_PHASES.length]
		);

		const generateNutrientValues = (): number[] => {
			const raw = Array.from({ length: 12 }, () => Math.random() + 0.1);
			const total = raw.reduce((a, b) => a + b, 0);
			const scaled = raw.map((v) => Math.round((v / total) * 1000) / 10);
			const currentSum = scaled.reduce((a, b) => a + b, 0);
			scaled[11] = Math.round((scaled[11] + (100 - currentSum)) * 10) / 10;
			return scaled;
		};

		const floraData: ParcelData = {
			phase: floraPhases,
			...Object.fromEntries(NUTRIENTS.map((n) => [n, generateNutrientValues()])),
		} as ParcelData;

		updateParcels(floraData);

		const cells: { nutrient: string; month: number }[] = [];
		for (let month = 0; month < 12; month++) {
			for (const nutrient of NUTRIENTS) {
				cells.push({ nutrient, month });
			}
		}

		const progressiveParcels = createEmptyParcelData();
		progressiveParcels.phase = [...floraPhases];

		const startDelay = 800;

		cells.forEach((cell, index) => {
			const timer = setTimeout(
				() => {
					const key = cell.nutrient as keyof Omit<ParcelData, 'phase'>;
					progressiveParcels[key][cell.month] = floraData[key][cell.month];
					setFloraParcels({ ...progressiveParcels });

					if (index === cells.length - 1) {
						setIsFloraAnimating(false);
					}
				},
				startDelay + index * 50
			);
			floraTimersRef.current.push(timer);
		});
	}, [updateParcels]);

	const handleTabChange = useCallback(
		(tab: 'manual' | 'flora') => {
			setActiveTab(tab);
			if (tab === 'flora') {
				startFloraFill();
			} else {
				floraTimersRef.current.forEach(clearTimeout);
				floraTimersRef.current = [];
				setIsFloraAnimating(false);
			}
		},
		[startFloraFill]
	);

	const handleFloraSendMessage = useCallback(
		(_message: string) => {
			setActiveTab('flora');
			setIsFloraExpanded(false);
			startFloraFill();
		},
		[startFloraFill]
	);

	const handleFloraClick = useCallback(() => {
		setActiveTab('flora');
		startFloraFill();
	}, [startFloraFill]);

	const handleEdit = useCallback(() => {
		if (!permissions.canEdit) return;
		if (selectedParcelling && !canEditParcelling(selectedParcelling)) {
			toast.error('Você não pode editar este parcelamento');
			return;
		}
		setIsEditing(true);
	}, [permissions.canEdit, selectedParcelling, canEditParcelling, toast]);

	const handleCancel = useCallback(async () => {
		if (isDirty) {
			const confirmed = await confirm({
				title: 'Descartar alterações',
				message: 'Você tem alterações não salvas. Deseja realmente cancelar?',
				confirmText: 'Descartar',
				cancelText: 'Continuar editando',
				variant: 'warning',
			});
			if (!confirmed) return;
		}

		if (selectedParcelling?._id.startsWith('temp-')) {
			const fallback = parcellings.find((p) => p._id !== selectedParcelling._id);
			removeParcelling(selectedParcelling._id);
			if (fallback) {
				selectParcelling(fallback._id);
			}
		} else {
			reset();
		}
		setIsEditing(false);
		setActiveTab('manual');
	}, [
		isDirty,
		selectedParcelling,
		parcellings,
		removeParcelling,
		selectParcelling,
		reset,
		confirm,
	]);

	const handleDelete = useCallback(async () => {
		if (!selectedParcelling || !permissions.canDelete) return;
		const confirmed = await confirm({
			title: 'Excluir parcelamento',
			message: `Tem certeza que deseja excluir "${selectedParcelling.name}"? Esta ação não pode ser desfeita.`,
			confirmText: 'Excluir',
			cancelText: 'Cancelar',
			variant: 'danger',
		});
		if (!confirmed) return;
		try {
			await deleteParcellingMutation.mutateAsync(selectedParcelling._id);
			removeParcelling(selectedParcelling._id);
			toast.success('Parcelamento excluído com sucesso');
			setIsEditing(false);
		} catch {
			toast.error('Erro ao excluir parcelamento');
		}
	}, [selectedParcelling, permissions.canDelete, confirm, deleteParcellingMutation, removeParcelling, toast]);

	const handleDuplicate = useCallback(async () => {
		if (!selectedParcelling || !permissions.canDuplicate) return;
		if (selectedParcelling._id.startsWith('temp-')) return;

		try {
			await duplicateParcellingMutation.mutateAsync({ id: selectedParcelling._id, amount: 1 });
			toast.success('Parcelamento duplicado com sucesso');
		} catch (error) {
			toast.error(error instanceof Error ? error.message : 'Erro ao duplicar parcelamento');
		}
	}, [selectedParcelling, permissions.canDuplicate, duplicateParcellingMutation, toast]);

	const handleAddNew = useCallback(async () => {
		if (!permissions.canCreate) return;

		const newParcelling = await createParcelling(
			{
				name: '',
				culture: '' as Culture,
				cropStage: '' as CropStage,
				note: '',
				isPublic: false,
				bindingUserIds: autoSelectSelfAsClient && currentUserId ? [currentUserId] : [],
				fixedParcel: toFixedParcelPayload(createEmptyParcelData()),
			},
			false
		);
		if (newParcelling) {
			selectParcelling(newParcelling._id);
			setIsEditing(true);
		}
	}, [permissions.canCreate, createParcelling, selectParcelling, autoSelectSelfAsClient, currentUserId]);

	const handleSelectParcelling = useCallback(
		(id: string) => {
			if (selectedParcelling?._id.startsWith('temp-') && selectedParcelling._id !== id) {
				removeParcelling(selectedParcelling._id);
			}
			selectParcelling(id);
			setIsEditing(false);
			setActiveTab('manual');
		},
		[selectParcelling, selectedParcelling, removeParcelling]
	);

	const handleFieldChange = useMemo(
		() => ({
			name: (value: string) => updateField('name', value),
			culture: (value: Culture) => updateField('culture', value),
			cropStage: (value: CropStage) => updateField('cropStage', value),
			note: (value: string) => updateField('note', value),
			bindingUserIds: (value: string[]) => updateField('bindingUserIds', value),
			isPublic: (value: boolean) => updateField('isPublic', value),
		}),
		[updateField]
	);

	return {
		activeTab,
		handleTabChange,
		isEditing,
		permissions,
		parcellings,
		selectedParcellingId,
		selectedParcelling,
		formData,
		errors,
		isDirty,
		isSubmitting,
		updateParcels,
		handleSubmit,
		handleFloraClick,
		handleEdit,
		handleCancel,
		handleDelete,
		handleDuplicate,
		handleAddNew,
		handleSelectParcelling,
		handleFieldChange,
		hasData: parcellings.length > 0,
		selectedCanEdit,
		selectedCanDelete,
		canBind,
		canBindFarm,
		bindingUsers,
		bindingUsersLoading,
		bindingSearchTerm,
		setBindingSearchTerm,
		bindingUserDisplayName,
		isAdmin,
		canManagePublicVisibility,
		clientModalOpen,
		farmModalOpen,
		handleOpenClientModal,
		handleCloseClientModal,
		handleClientSelect,
		handleOpenFarmModal,
		handleCloseFarmModal,
		handleFarmSelect,
		farms,
		farmsLoading,
		selectedFarmIds,
		selectedFarmName,
		isFloraExpanded,
		isFloraAnimating,
		floraParcels,
		updateFloraParcels,
		handleToggleFloraExpand,
		handleFloraSendMessage,
	};
};
